/* eslint-disable no-console */
import { calcularImporteNetoIntermediario } from "."; // ajustá la ruta si es necesario
import { AGENCIAS_DIRECTAS, SIN_COMISION_2025 } from "../constants/permisos";
import { getApxMonto } from "./apx";
import { calcularComisionEdificiosTotal, keyDeFactura, resolverFechaFactura } from "./comisionEdificiosUtil";
import { getImporteFacturable } from "./getImporteFacturable";
import { resolverPorcentajeAgencia } from "./resolverPorcentajeAgencia";
import { resolverPorcentajeBroker } from "./resolverPorcentajeBroker";

// Fecha de corte para ID 32
const EXECUTIVE_CUTOFF_DATE = new Date("2025-06-01");

// Usa resolverFechaFactura centralizada y retorna Date
const resolverFechaComision = (op) => new Date(resolverFechaFactura(op));

// Núcleo ÚNICO de la comisión comercial por ejecutivo (10.1): calcula el neto (importe facturable menos
// agencia, edificios, take, APX y broker) y aplica la escala por país/ejecutivo/fecha, devolviendo las
// entradas REALES [{ ejecutivo, percentage, comision }] (sin el padding 0% que es solo de display).
// calculateSalesCommission suma las comisiones; getSalesCommissionBreakdown le agrega el ejecutivo con
// 0% si no quedó en la lista. Antes esta lógica (neto + escala) estaba duplicada literal en ambas.
const desgloseComisionComercial = (
  opportunity,
  clientes,
  intermediarios,
  edificios,
  tipoCambio,
  snapMap = null,
  takeLocal = 0,
  pctMap = null
) => {
  // MONTO CONGELADO (fuente única = comision_snapshot, leído por pctMap): si la venta/contrato-mes está
  // SELLADA (pctMap tiene su key — el sello es `pct_congelado_en IS NOT NULL`, mismo criterio que ya usa
  // el % congelado) y hay monto congelado, la BASE se toma de la foto (`monto`/`descuento` al facturar)
  // en vez del importe vivo. Así editar el importe de una venta ya facturada NO mueve su comisión. Sin
  // foto o sin monto congelado → `opEfectiva === opportunity` → importe vivo, comportamiento idéntico a
  // hoy para ventas sin facturar. Todo el resto de campos (ejecutivo, mes_cierre, país, edificios, etc.)
  // sigue viniendo de la venta; solo se overridea la base monetaria.
  const congelado = pctMap?.get?.(keyDeFactura(opportunity));
  const opEfectiva =
    congelado && congelado.monto != null
      ? { ...opportunity, importe: congelado.monto, descuento_acuerdo_pct: congelado.descuento }
      : opportunity;

  const agencyCommission = calculateComission.calculateAgencyCommission(
    opEfectiva,
    clientes,
    intermediarios,
    tipoCambio,
    pctMap
  );
  const netoDespuesAgencias = calcularImporteNetoIntermediario(
    opEfectiva,
    Object.values(clientes),
    Object.values(intermediarios),
    opEfectiva.pais,
    pctMap
  );
  // FASE 6 (capa aislada, regla 10.48): el take programático baja la base de comisión de edificios y la
  // de comisión comercial. `takeLocal` viene del call site ya en moneda local (getTakeLocal). Default 0.
  // APX (Publicis, etapa 2): capa paralela auto-resuelta desde opportunity.apx_id. `getApxMonto` → 0 si
  // apx_id es null (inerte). Broker AdLab (2ª capa, 7.13): baja la base igual que agencia/APX/edificios;
  // % post-gate (congelado si hay pctMap) de la fuente única, 0 si no aplica.
  const apxLocal = getApxMonto(opEfectiva, intermediarios, pctMap);
  const brokerPct = resolverPorcentajeBroker(
    opEfectiva,
    Object.values(clientes),
    Object.values(intermediarios),
    resolverFechaComision(opEfectiva),
    pctMap
  );
  const brokerLocal = (getImporteFacturable(opEfectiva) * brokerPct) / 100;
  const buildingCommission = calcularComisionEdificiosTotal(
    opEfectiva,
    edificios,
    netoDespuesAgencias,
    snapMap,
    takeLocal,
    apxLocal
  );
  // Base = importe FACTURABLE (7.22): con descuento de acuerdo baja a importe×(1−desc); sin él, el importe.
  const importeBruto = getImporteFacturable(opEfectiva);
  const netoFinal = importeBruto - agencyCommission - buildingCommission - (takeLocal || 0) - apxLocal - brokerLocal;
  // CORTE DURO → null (no un array): distingue "no aplica comisión" (sin padding de display) de
  // "válido pero $0" (desglose vacío → con padding). En el original, estos return corrían ANTES del
  // padding de getSalesCommissionBreakdown, así que ese caso NO recibía la fila 0% del ejecutivo.
  if (netoFinal <= 0) return null;

  // Parseo de mes_cierre "MM-YYYY"
  let monthCierre = null;
  let yearCierre = null;
  if (opportunity.mes_cierre) {
    const [mesStr, anoStr] = opportunity.mes_cierre.split("-");
    monthCierre = parseInt(mesStr, 10) - 1; // 01 → 0
    yearCierre = parseInt(anoStr, 10);
  }

  const fechaFactura = resolverFechaComision(opportunity);
  const ejecutivoId = parseInt(opportunity.ejecutivo);

  // Exclusión ID 32 (aplica a ambos países) — corte duro
  if (ejecutivoId === 32 && fechaFactura >= EXECUTIVE_CUTOFF_DATE) return null;

  const desglose = [];

  if (opportunity.pais === "Perú") {
    // Priscilla (ID 7): siempre 1.25% universal
    desglose.push({ ejecutivo: 7, percentage: 1.25, comision: netoFinal * 0.0125 });

    // ID 6: 3.75% si mes_cierre <= junio 2025 (dejó la empresa)
    if (yearCierre === 2025 && monthCierre <= 5) {
      desglose.push({ ejecutivo: 6, percentage: 3.75, comision: netoFinal * 0.0375 });
    }

    // Karin (ID 306): 4% cuentas bajo su gestión, 1% resto — desde julio 2025. Corte cuentas cedidas
    // (7.13): si la venta paga broker (brokerPct > 0), la interna de Karin es 0 — mutuamente excluyente
    // con el 10% de AdLab (corta 4% y 1%; respeta excluir_broker/históricas por el mismo resolver).
    if (brokerPct <= 0 && ((yearCierre === 2025 && monthCierre >= 6) || yearCierre > 2025)) {
      const clienteAsociado = clientes[opportunity.client_id];
      const esGestionKarin = clienteAsociado && String(clienteAsociado.ejecutivo) === "306";
      const percentage306 = esGestionKarin ? 4 : 1;
      desglose.push({ ejecutivo: 306, percentage: percentage306, comision: netoFinal * (percentage306 / 100) });
    }
  } else if (opportunity.pais === "Chile") {
    // Año de facturación: facturafecha real, o año planificado (ano) para pendientes
    const yearFactura = opportunity.facturafecha
      ? fechaFactura.getFullYear()
      : parseInt(opportunity.ano, 10) || fechaFactura.getFullYear();

    if (yearFactura <= 2025) {
      // Lógica 2025: escala descendente por trimestre — cortes duros → null (sin padding)
      if (!opportunity.ejecutivo) return null;
      if (SIN_COMISION_2025.includes(ejecutivoId)) return null;

      const monthFactura = fechaFactura.getMonth();
      let percentage = 0;
      if (yearFactura === 2025) {
        if (monthFactura <= 2) percentage = 8;
        else if (monthFactura <= 5) percentage = 7;
        else if (monthFactura <= 8) percentage = 6;
        else percentage = 5;
      }
      if (percentage > 0) {
        desglose.push({ ejecutivo: ejecutivoId, percentage, comision: netoFinal * (percentage / 100) });
      }
    } else {
      // 2026+: Nicolás (904) cobra solo en ventas CERRADAS (mes_cierre) en 2026+
      if (yearCierre && yearCierre >= 2026) {
        // Líder de la cuenta (tramo 4%/1%): si la foto está SELLADA y tiene líder congelado (Chile,
        // regla 10.287/nueva), se usa ESE valor — cambiar clientes.ejecutivo después de facturar no
        // recalcula retroactivamente. Sin foto o sin líder congelado (fila vieja, o venta sin facturar
        // todavía) → se resuelve EN VIVO con clientes.ejecutivo, comportamiento idéntico a hoy.
        const liderCongelado = congelado?.ejecutivoLiderCongelado;
        const esGestionNicolas =
          liderCongelado != null
            ? liderCongelado === 904
            : (() => {
                const clienteAsociado = clientes[opportunity.client_id];
                return clienteAsociado && String(clienteAsociado.ejecutivo) === "904";
              })();
        const percentage = esGestionNicolas ? 4 : 1;
        desglose.push({ ejecutivo: 904, percentage, comision: netoFinal * (percentage / 100) });
      }
      // Ventas cerradas en 2025 facturadas en 2026+: $0 comisión comercial
    }
  }

  return desglose;
};

export const calculateComission = {
  /**
   * Comisión de agencia (intermediario).
   * Preferir pasar opportunity con importe ya en moneda local (oppEnLocal).
   * tipoCambio es legacy — solo lo usa VentasFacturasEjecutivo.
   */
  calculateAgencyCommission: (opportunity, clientes, intermediarios, tipoCambio, pctMap = null) => {
    let comisionIntermediarioPorcentaje = 0;
    // Anti-doble-descuento (7.22 Paso 2): con descuento de acuerdo, sin comisión de agencia
    // (el 25% ya salió como reducción de base vía getImporteFacturable).
    if ((parseFloat(opportunity.descuento_acuerdo_pct) || 0) > 0) return 0;
    if (!opportunity.isBTL) {
      // % de agencia desde la fuente única (Fase A0); con pctMap (A3) lee el % congelado.
      comisionIntermediarioPorcentaje = resolverPorcentajeAgencia(
        opportunity,
        clientes,
        intermediarios,
        opportunity.pais,
        pctMap
      );
    } else {
      comisionIntermediarioPorcentaje = opportunity.comision || 0;
      if (AGENCIAS_DIRECTAS.includes(opportunity.intermediarioId || opportunity.intermediario))
        comisionIntermediarioPorcentaje = 0;
    }
    const importeBruto = parseFloat(opportunity.importe) || 0;
    const comision = importeBruto * (comisionIntermediarioPorcentaje / 100);
    // Legacy: si el caller pasa tipoCambio y la opp está en USD, convierte el resultado
    if (tipoCambio && opportunity.moneda_dolar) return comision * tipoCambio;
    return comision;
  },

  /**
   * Comisión comercial total (para ER y cálculos).
   * Preferir pasar opportunity con importe ya en moneda local (oppEnLocal).
   * tipoCambio es legacy — solo lo usa VentasFacturasEjecutivo.
   */
  calculateSalesCommission: (
    opportunity,
    clientes,
    intermediarios,
    edificios,
    tipoCambio,
    snapMap = null,
    takeLocal = 0,
    pctMap = null
  ) => {
    // Fuente única (10.1): la comisión total es la suma de las entradas del desglose. Corte duro (null)
    // o desglose vacío → 0; el padding 0% de getSalesCommissionBreakdown es solo de display y no suma.
    const desglose = desgloseComisionComercial(
      opportunity,
      clientes,
      intermediarios,
      edificios,
      tipoCambio,
      snapMap,
      takeLocal,
      pctMap
    );
    if (!desglose) return 0;
    return desglose.reduce((sum, d) => sum + (d.comision || 0), 0);
  },

  /**
   * Desglose de comisión comercial para modal (array de objetos).
   * Preferir pasar opportunity con importe ya en moneda local (oppEnLocal).
   * tipoCambio es legacy — solo lo usa VentasFacturasEjecutivo.
   */
  getSalesCommissionBreakdown: (
    opportunity,
    clientes,
    intermediarios,
    edificios,
    tipoCambio,
    snapMap = null,
    takeLocal = 0,
    pctMap = null
  ) => {
    // Fuente única (10.1): mismo núcleo que calculateSalesCommission; acá se agrega el padding de display.
    const desglose = desgloseComisionComercial(
      opportunity,
      clientes,
      intermediarios,
      edificios,
      tipoCambio,
      snapMap,
      takeLocal,
      pctMap
    );
    // Corte duro (null) → sin comisión ni padding (netoFinal<=0, ID 32, guards 2025): [] como el original.
    if (!desglose) return [];
    // Si el ejecutivo de la oportunidad no está en el desglose, agregarlo con 0%
    // (ej: Sergio cerró la venta pero dejó la empresa → aparece con $0)
    const ejecutivoId = parseInt(opportunity.ejecutivo);
    if (ejecutivoId && !desglose.some((d) => d.ejecutivo === ejecutivoId)) {
      desglose.unshift({ ejecutivo: ejecutivoId, percentage: 0, comision: 0 });
    }

    return desglose;
  },
};
